import React from 'react';
import { useSelector } from 'react-redux';

import ImageLazy from '@common/react/components/UI/ImageLazy/ImageLazy';

import Topics from '@app/components/Pages/Speaking/Topics';
import Background from '@app/components/UI/Background/Background';
import Card from '@app/components/UI/Card/Card';
import HelmetProvider from '@app/components/UI/Helmet/HelmetCustom';
import Line2 from '@app/components/UI/Lines/Line2';
import Line3 from '@app/components/UI/Lines/Line3';
import PhotoGallery from '@app/components/UI/PhotoGallery/PhotoGallery';
import SelectedText from '@app/components/UI/SelectedText';
import Slide from '@app/components/UI/Slide';
import SpeakingForm from '@app/components/UI/SpeakingForm/SpeakingForm';
import CustomSwiper from '@app/components/UI/Swiper/CustomSwiper';
import GalleryPrint from '@app/imgs/GalleryPrint';
import { CustomSlideProps } from '@app/objects/CustomSlideProps';
import { Event } from '@app/objects/Event';
import { ApplicationState } from '@app/store';
import { addEvents } from '@app/utils/addEvents';

import '@app/scss/components/speakingBlock.scss';
import '@app/scss/pages/speaking.scss';

const items = [
	{
		name: 'Practical Care for the Geriatric Veterinary Patient',
		text: '',
	},
	{
		name: '5 Ingredients for Veterinary Hospice',
		text: '',
	},
	{
		name: 'Endings Matter Most – making the euthanasia better for all involved',
		text: '',
	},
	{
		name: 'Get your veterinary groove back',
		text: `
			Being in the veterinary world is amazing and the dream of many people. But the reality and statistics are not so wonderful with many facing burnout,
			client compliance frustration, compassion fatigue and even much worse. We must not let our dreams become buried under stress, discouragement, rejection,
			fatigue and negative voices. Instead learn how to find fulfillment, passion and love for veterinary medicine again – regardless of the path you take
			in this industry. Your dream may be buried, but the good news is that it’s still alive. (1 hour)
		`,
	},
	{
		name: 'Geriatric Pet Care Management in the Home',
		text: '',
	},
	{
		name: 'Helping Owners with Assessing Quality of Life',
		text: '',
	},
	{
		name: 'Caregiver Stress, Burden & Relief - When loving hurts',
		text: '',
	},
	{
		name: 'Managing mobility issues in geriatric patients',
		text: '',
	},
	{
		name: 'Faces of Death – how to deal with owners dealing with death',
		text: '',
	},
	{
		name: 'Where are all the Grey Muzzles? Marketing before it is too late',
		text: '',
	},
	{
		name: 'Serious Illness Conversations and Guidance with Pet Parents',
		text: '',
	},
	{
		name: 'What would you do? The euthanasias that pull at your heart strings',
		text: '',
	},
	{
		name: 'Anticipatory Grief & Pet Loss Preparation',
		text: '',
	},
	{
		name: 'My emotional candle is lit and it smells of anxiety',
		text: '',
	},
];

const slidesGallery = [
	{
		alt: 'img1',
		src: '/images/Speaking_MorePhotos_1.jpeg',
	},
	{
		alt: 'img2',
		src: '/images/Speaking_MorePhotos_2.jpeg',
	},
	{
		alt: 'img3',
		src: '/images/Speaking_MorePhotos_3.jpeg',
	},
	{
		alt: 'img4',
		src: '/images/Speaking_MorePhotos_4.jpeg',
	},
];

const Speaking: React.FC = () => {
	const upcomingEvents: Array<Event> = useSelector((state: ApplicationState) => state.upcomingEvents.items);
	const prevEvents: Array<Event> = useSelector((state: ApplicationState) => state.prevEvents.items);
	const [upcoming, setUpcoming] = React.useState<Array<CustomSlideProps>>();
	const [prev, setPrev] = React.useState<Array<CustomSlideProps>>();
	const [showGallery, setShowGallery] = React.useState<boolean>(false);

	React.useEffect(() => {
		setUpcoming(addEvents(upcomingEvents));
		setPrev(addEvents(prevEvents));
	}, []);

	return (
		<HelmetProvider title="Speaking">
			<div className="speaking-page is-relative pages">
				<Background />
				<div>
					{upcoming && prev && (
						<CustomSwiper
							events
							upcoming={upcoming.map((item) => (
								<Slide key={item.text} {...item} />
							))}
							prev={prev.map((item) => (
								<Slide key={item.text} {...item} />
							))}
						/>
					)}
					<div className="container">
						<div className="speaking">
							<Card
								image={
									<>
										<h2>
											<SelectedText
												text="Speaking"
												line={<Line3 />}
											/>
										</h2>
										<h3>Energetic, engaging and inspiring keynotes and workshops for veterinary teams and pet owners!</h3>
										<p className="h4">
											Dr. Mary is a sought-after speaker who engages both professional and lay audiences in insightful
											discussions on geriatric medicine, quality of life assessment, hospice, and end-of-life care. With
											a wealth of expertise, she has delivered captivating keynote addresses on topics spanning
											entrepreneurship, leadership, motivation, and discovering one's unique path. Her impactful
											presentations have graced renowned events and venues including VMX, AVMA, WVC, Fetch, state VMA
											conventions, IAAHPC gatherings, professional sales meetings, and prestigious veterinary schools
											and animal hospitals across the globe.
										</p>
										<div className="is-relative">
											<ImageLazy alt="image" width={729} height={410} src="/images/img2.png" />
											<button className="btn btn-default" onClick={() => setShowGallery(true)}>
												<GalleryPrint />
												<span>More Photos</span>
											</button>
											<PhotoGallery slides={slidesGallery} show={showGallery} setShow={setShowGallery} />
										</div>
									</>
								}
								text={
									<>
										<SpeakingForm />
									</>
								}
							/>
						</div>
					</div>
				</div>
				<div className="speaking-topics">
					<div className="container">
						<h2>
							Speaking
							<SelectedText text=" Topics" line={<Line2 />} />
						</h2>
						<p className="h4">
							Dr. Mary has a&nbsp;wide range of&nbsp;topics she can speak on&nbsp;which can be&nbsp;tailored to&nbsp;the
							audience&nbsp;— whether that is&nbsp;veterinarians, shelter workers, practice managers or&nbsp;pet parents. Here
							is&nbsp;just a&nbsp;sampling of&nbsp;the topics she speaks&nbsp;on.
						</p>
						<Topics
							items={items.map((item) => ({
								...item,
								text: item.text,
							}))}
						/>
					</div>
				</div>
			</div>
		</HelmetProvider>
	);
};

export default Speaking;
